/**
 * Dashboard — multi-repo overview (Phase 10).
 *
 * One card per managed repo with live operational state pulled from the
 * Worker: per-repo KV `status` blob (agent count, PR count, active
 * sprint id, env health) + the latest `session_handoffs` row's
 * `current_state` line. A small summary tile below the grid keeps the
 * pre-Phase-10 global counts (features in flight, active sprints, KB
 * entries) so the data isn't lost.
 *
 * Two-tier loader: repo list + global counts go through Supabase
 * (RLS-gated, fast); per-repo live state goes through the Worker
 * (the only surface that holds the KV status blob + D1 handoff row).
 * Worker calls are best-effort per repo — a single failure renders
 * an inline note on that card instead of failing the whole page.
 */

import { Link, useLoaderData } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@repo-manager/ui';

import { ApiError, getApi, type HandoffSummary, type RepoStatusBlob } from '../lib/api';
import { getSupabase } from '../lib/supabase';

interface RepoCard {
  id: string;
  owner: string;
  name: string;
  lastActiveAt: string | null;
  status: RepoStatusBlob | null;
  handoff: HandoffSummary | null;
  /**
   * Set when a per-repo Worker fetch failed. The dashboard reads via
   * Supabase (RLS-gated) for the list itself; the Worker still owns the
   * `status` blob (KV) + `session_handoffs` (D1), but those require
   * an API-key bearer and the web UI carries a Supabase JWT. Until a
   * JWT-to-worker bridge lands, every per-card fetch will 401 — we
   * surface that as "live metrics unavailable" rather than a red error.
   */
  liveMetricsUnavailable: boolean;
  /** Specific error string if the failure wasn't a vanilla 401. */
  error: string | null;
}

interface DashboardData {
  repos: RepoCard[];
  globals: {
    inFlightFeatures: number;
    activeSprints: number;
    activeKbEntries: number;
    draftKbEntries: number;
  };
}

export async function dashboardLoader(): Promise<DashboardData> {
  const supabase = getSupabase();
  const api = getApi();

  // Phase 1 — repo list + global counts via Supabase (RLS-gated, single
  // source of truth shared with the `<ReposProvider>` header chip).
  // Going through the Worker would 401 today: the web UI ships a
  // Supabase JWT, but `/api/repos` requires an API key (see
  // `apps/worker/src/middleware/auth.ts`).
  const [reposQ, featuresQ, sprintsQ, kbActiveQ, kbDraftQ] = await Promise.all([
    supabase
      .from('repos')
      .select('id, owner, name, last_active_at')
      .order('last_active_at', { ascending: false, nullsFirst: false }),
    supabase
      .from('features')
      .select('*', { count: 'exact', head: true })
      .in('status', ['approved', 'in_progress']),
    supabase.from('sprints').select('*', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('kb_entries').select('*', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('kb_entries').select('*', { count: 'exact', head: true }).eq('status', 'draft'),
  ]);

  const repoRows = reposQ.data ?? [];

  // Phase 2 — best-effort per-repo live metrics from the Worker. These
  // hit KV (`status`) and D1 (`handoff`) which the Supabase JWT can't
  // currently authenticate against. Every failure is silently mapped
  // to "live metrics unavailable" so a healthy Supabase read still
  // produces a useful card.
  const cards: RepoCard[] = await Promise.all(
    repoRows.map(async (r) => {
      const [statusRes, handoffRes] = await Promise.allSettled([
        api.reposStatus(r.id),
        api.reposHandoff(r.id),
      ]);
      const status = statusRes.status === 'fulfilled' ? statusRes.value.status : null;
      const handoff =
        handoffRes.status === 'fulfilled' && handoffRes.value
          ? handoffRes.value.handoff
          : null;
      const failure =
        statusRes.status === 'rejected'
          ? statusRes.reason
          : handoffRes.status === 'rejected'
            ? handoffRes.reason
            : null;
      // 401 is the expected path until a JWT bridge lands; everything
      // else surfaces as an inline `! <msg>` so we notice real outages.
      const expectedUnauth =
        failure instanceof ApiError && (failure.status === 401 || failure.status === 403);
      return {
        id: r.id,
        owner: r.owner,
        name: r.name,
        lastActiveAt: r.last_active_at,
        status,
        handoff,
        liveMetricsUnavailable: !status && !handoff,
        error:
          failure && !expectedUnauth
            ? failure instanceof Error
              ? failure.message
              : String(failure)
            : null,
      };
    }),
  );

  return {
    repos: cards,
    globals: {
      inFlightFeatures: featuresQ.count ?? 0,
      activeSprints: sprintsQ.count ?? 0,
      activeKbEntries: kbActiveQ.count ?? 0,
      draftKbEntries: kbDraftQ.count ?? 0,
    },
  };
}

export function DashboardPage() {
  const data = useLoaderData() as DashboardData;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          {data.repos.length} managed repo{data.repos.length === 1 ? '' : 's'}.
        </p>
      </div>

      {data.repos.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No managed repos yet. Insert a row into <code>public.repos</code> and
            run <code>infra migrate</code> to onboard one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.repos.map((r) => (
            <RepoCardView key={r.id} card={r} />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium text-muted-foreground">
            Across all repos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Stat label="Features in flight" value={data.globals.inFlightFeatures} href="/roadmap" />
            <Stat label="Active sprints" value={data.globals.activeSprints} href="/sprints" />
            <Stat label="Active KB entries" value={data.globals.activeKbEntries} href="/knowledge" />
            <Stat
              label="KB drafts pending"
              value={data.globals.draftKbEntries}
              href="/knowledge/drafts"
              highlight={data.globals.draftKbEntries > 0}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
          <CardDescription>Get to the things you do most.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/features/new">+ New feature</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/sprints/new">+ Plan sprint</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/knowledge/new">+ KB entry</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function RepoCardView({ card }: { card: RepoCard }) {
  const status = card.status;
  const handoff = card.handoff;
  const agents = status?.agent_count ?? handoff?.agent_count ?? 0;
  const prs = status?.pending_pr_count ?? handoff?.pending_pr_count ?? 0;
  const sprintId = handoff?.active_sprint_id ?? status?.active_sprint_id ?? null;
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-base">
          <code className="font-mono text-sm">
            {card.owner}/{card.name}
          </code>
        </CardTitle>
        <CardDescription className="text-xs">
          {card.lastActiveAt
            ? `last active ${formatAge(card.lastActiveAt)} ago`
            : '(never active)'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Metric label="agents" value={agents} />
          <Metric label="open PRs" value={prs} />
          <Metric label="sprint" value={sprintId ? sprintId.slice(0, 8) : '—'} />
        </div>
        {handoff?.current_state && (
          <div className="rounded-md bg-muted/40 p-2 text-xs">
            <div className="font-medium text-muted-foreground">current state</div>
            <div className="mt-1 whitespace-pre-wrap">
              {handoff.current_state.length > 240
                ? `${handoff.current_state.slice(0, 240)}…`
                : handoff.current_state}
            </div>
          </div>
        )}
        {!handoff && card.liveMetricsUnavailable && !card.error && (
          <div className="text-xs text-muted-foreground">
            Live metrics unavailable (Worker JWT bridge pending — Phase 11).
          </div>
        )}
        {!handoff && !card.liveMetricsUnavailable && (
          <div className="text-xs text-muted-foreground">
            (no handoff yet — the next cycle will fill this in)
          </div>
        )}
        {card.error && (
          <div className="text-xs text-destructive">! {card.error}</div>
        )}
        <div className="flex justify-end pt-1">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/sprints?repo=${card.id}`}>Open sprint →</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border bg-card p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  href,
  highlight,
}: {
  label: string;
  value: number | null;
  href: string;
  highlight?: boolean;
}) {
  return (
    <Link
      to={href}
      className={
        'group rounded-md border p-3 transition-colors hover:bg-muted/50 ' +
        (highlight ? 'border-amber-500/40 bg-amber-50/30' : '')
      }
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">
        {value === null ? <Skeleton className="h-7 w-12" /> : value}
      </div>
    </Link>
  );
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}
